import React, { useEffect, useState } from "react";
import { apiRequest } from "../../../service/apiRequest";
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Select,
  DatePicker,
} from "antd";
import { createStyles } from "antd-style";
import { HomeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { theme } from "antd";
import Voucher2Model from "./Voucher2Model";
import { showSuccess, showError } from "../../../components/errorHandler";
import type { PopconfirmProps, TableColumnsType } from "antd";
import { useCurrentModule } from "../../../hooks/useCurrentModule";
import dayjs from "dayjs";

import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useSearchParams } from "react-router-dom";
import type { RangePickerProps } from "antd/es/date-picker";

import { saveAs } from "file-saver";

const { RangePicker } = DatePicker;
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Vouchers2 {
  voucher_id: number;
  voucher_no: number;
  ledger_name: string;
  voucher_type: string;
  voucher_amount: number;
  voucher_status: number;
  due_date?: string;
  voucher_date: string;
  voucher_reference: string;
  voucher_note: string;
  voucher_account_from: string;
  voucher_account_to: string;
  adjust_bill?: number;
}

const Vouchers2: React.FC = () => {
  const [Vouchers2, setVouchers] = useState<Vouchers2[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Vouchers2 | null>(null);
  const { styles } = useStyle();
  const currentModule = useCurrentModule();

  const [searchParams, setSearchParams] = useSearchParams();

  const voucherType = searchParams.get("voucher_type") || "";
  const voucherDate = searchParams.get("voucher_date") || "";
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const [filterType, setFilterType] = useState<string>(voucherType);
  const [filterDate, setFilterdate] = useState<string>(voucherDate);
  const [filterVouchers, setFilterVouchers] = useState<Vouchers2[]>([]);
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
    total: number;
  }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const handleVoucherTypeChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("voucher_type", value);
    if (filterDate) {
      params.set("voucher_date", filterDate);
    }
    setSearchParams(params);
    setFilterType(value);
  };

  const cancel: PopconfirmProps["onCancel"] = (e) => {
    console.log(e);
    showError("Click on No");
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchVoucher = async (
    page = 1,
    limit = 10,
    voucher_type: string | null = null,
    voucher_date: string | null = null
  ) => {
    try {
      const response = await apiRequest<{
        data: Vouchers2[];
        page: number;
        voucher_type: string;
        voucher_date: string;
        limit: number;
        total: number;
      }>({
        method: "get",
        url: `Vouchers2?page=${page}&limit=${limit}&voucher_type=${filterType}&voucher_date=${filterDate}`,
        data: {
          voucher_type: voucherType,
        },
      });
      console.log("data", response.data);
      setVouchers(response.data);
      setPagination({
        current: response.page,
        pageSize: response.limit,
        total: response.total,
      });
    } catch {
      showError("Failed to fetch Vouchers2");
    }
  };

  useEffect(() => {
    fetchVoucher(
      pagination.current,
      pagination.pageSize,
      filterType,
      filterDate
    );
  }, [searchParams]);

  const handleEditClick = (record: Vouchers2) => {
    setIsEditMode(true);
    setInitialValues({
      voucher_id: record.voucher_id,
      ledger_name: record.ledger_name,
       voucher_no: record.voucher_no,
      voucher_type: record.voucher_type,
      voucher_amount: record.voucher_amount,
      voucher_date: record.voucher_date,
      voucher_reference: record.voucher_reference,
      voucher_note: record.voucher_note,
      voucher_account_from: record.voucher_account_from,
      voucher_account_to: record.voucher_account_to,
      voucher_status: record.voucher_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchVoucher();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleTableChange: TableProps<Vouchers2>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const handleDateRangeChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    const params = new URLSearchParams(searchParams);

    if (!dates || !dates[0] || !dates[1]) {
      // If date is cleared, remove from filter and URL
      setFilterdate("");
      params.delete("voucher_date");
      setSearchParams(params);
      setFilterVouchers(Vouchers2); // Optional: reset filtered data
      return;
    }

    const [from, to] = dates;
    const dateRange = dateStrings.join(" - ");
    setFilterdate(dateRange);
    params.set("voucher_date", dateRange);

    if (filterType) {
      params.set("voucher_type", filterType);
    }

    setSearchParams(params);

    const filtered = Vouchers2.filter((item) => {
      const serviceDate = dayjs(item.voucher_date);
      return (
        serviceDate.isSameOrAfter(from, "day") &&
        serviceDate.isSameOrBefore(to, "day")
      );
    });

    setFilterVouchers(filtered);
  };
  const handleDownloadVoucher = async (voucher_id: number) => {
    try {
      const blob = await apiRequest<Blob>({
        method: "get",
        url: `/download-Voucher2/${voucher_id}`,
        config: { responseType: "blob" },
      });

      // Create filename (you can customize)
      const fileName = `DownloadVoucher_${voucher_id}.pdf`;

      // Use file-saver to save file
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Failed to download DownloadVoucher", error);
    }
  };

  const columns: TableColumnsType<Vouchers2> = [
    {
  title: "Voucher No",
  dataIndex: "voucher_no", // या voucher_id अगर वही इस्तेमाल करना है
  key: "voucher_no",
  width: 120,
  filters: Array.from(new Set(Vouchers2.map((s) => s.voucher_no ?? ""))).map(
    (no) => ({
      text: no,
      value: no,
    })
  ),
  filterSearch: true,
  filterMode: "tree",
  filteredValue: filteredInfo.voucher_no || null,
  onFilter: (value, record) =>
    (record.voucher_no ?? "").toString().includes(value as string),
  sorter: (a, b) =>
    (a.voucher_no ?? "").toString().localeCompare((b.voucher_no ?? "").toString()),
  sortOrder: sortedInfo.columnKey === "voucher_no" ? sortedInfo.order : null,
  sortDirections: ["ascend", "descend"],
  ellipsis: true,
  render: (_, record) => <div>{record.voucher_no ?? "-"}</div>,
},

{
  title: "Ledger Name",
  dataIndex: "ledger_name",
  key: "ledger_name",
  width: 200,

  filters: Array.from(
    new Set(Vouchers2.map((s) => s.ledger_name)) // unique ledger names निकालने के लिए
  )
    .filter(Boolean) // null/undefined हटाने के लिए
    .map((name) => ({
      text: String(name),
      value: String(name),
    })),

  onFilter: (value, record) =>
    String(record.ledger_name).toLowerCase() ===
    String(value).toLowerCase(),

  filterSearch: true,
  filterMode: "tree",
  filteredValue: filteredInfo.ledger_name || null,

  sortOrder:
    sortedInfo.columnKey === "ledger_name" ? sortedInfo.order : null,
  sortDirections: ["ascend", "descend"],
  ellipsis: true,
},

    {
      title: "Voucher Type",
      dataIndex: "voucher_type",
      key: "voucher_type",
      width: 130,
      filters: Array.from(new Set(Vouchers2.map((s) => s.voucher_type))).map(
        (type) => ({
          text: type,
          value: type,
        })
      ),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.voucher_type || null,
      onFilter: (value, record) =>
        record.voucher_type.includes(value as string),
      sorter: (a, b) => a.voucher_type.localeCompare(b.voucher_type),
      sortOrder:
        sortedInfo.columnKey === "voucher_type" ? sortedInfo.order : null,
      sortDirections: ["ascend", "descend"],
      ellipsis: true,
      render: (_, record) => <div>{record.voucher_type}</div>,
    },
   {
  title: "Voucher Amount(₹)",
  dataIndex: "voucher_amount",
  key: "voucher_amount",
  width: 130,

  // unique amounts निकालने के लिए
  filters: Array.from(new Set(Vouchers2.map((s) => s.voucher_amount)))
    .filter(Boolean)
    .map((amt) => ({
      text: String(amt),
      value: String(amt),
    })),

  filterSearch: true,
  filterMode: "tree",
  filteredValue: filteredInfo.voucher_amount || null,

  // filter logic
  onFilter: (value, record) =>
    String(record.voucher_amount) === String(value),

  // sorting numeric
  sorter: (a, b) => a.voucher_amount - b.voucher_amount,
  sortOrder:
    sortedInfo.columnKey === "voucher_amount" ? sortedInfo.order : null,
  sortDirections: ["ascend", "descend"],

  ellipsis: true,
  render: (_, record) => <div>{record.voucher_amount}</div>,
},

{
  title: "Due Days",
  dataIndex: "due_date",
  key: "due_days",
  width: 120,

  sorter: (a, b) => {
    const today = new Date().setHours(0, 0, 0, 0);
    const dateA = a.due_date ? new Date(a.due_date).setHours(0, 0, 0, 0) : today;
    const dateB = b.due_date ? new Date(b.due_date).setHours(0, 0, 0, 0) : today;
    return dateA - dateB;
  },
  sortOrder:
    sortedInfo.columnKey === "due_days" ? sortedInfo.order : null,
  sortDirections: ["ascend", "descend"],

  ellipsis: true,
  render: (_, record) => {
    if (!record.due_date) return null;
    const today = new Date().setHours(0, 0, 0, 0);
    const due = new Date(record.due_date).setHours(0, 0, 0, 0);

    const diffDays = Math.floor((due - today) / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return <div>{`${diffDays} days left`}</div>;
    } else if (diffDays === 0) {
      return <div style={{ color: "orange" }}>Due Today</div>;
    } else {
      return <div style={{ color: "red" }}>{`${Math.abs(diffDays)} days overdue`}</div>;
    }
  },
},


   {
  title: "Due Date",
  dataIndex: "due_date",
  key: "due_date",
  width: 100,
 filters: Array.from(
  new Set(
    Vouchers2.map((s) => s.due_date).filter((d): d is string => d !== undefined)
  )
).map((date) => ({
  text: date,
  value: date,
})),

  filterSearch: true,
  filterMode: "tree",
  filteredValue: filteredInfo.due_date || null,
onFilter: (value, record) =>
  (record.due_date ?? "").includes(value as string),

sorter: (a, b) =>
  (a.due_date ?? "").localeCompare(b.due_date ?? ""),

  sortOrder:
    sortedInfo.columnKey === "due_date" ? sortedInfo.order : null,
  sortDirections: ["ascend", "descend"],
  ellipsis: true,
  render: (_, record) => {
    if (!record.due_date) return null;
    const [year, month, day] = record.due_date.split("-");
    return <div>{`${day}-${month}-${year}`}</div>;
  },
},

   {
  title: "Type of Reference",
  dataIndex: "reference_type",
  key: "reference_type",
  width: 200,

  filterSearch: true,
  filterMode: "tree",
  filteredValue: filteredInfo.reference_type || null,

  sortOrder:
    sortedInfo.columnKey === "reference_type" ? sortedInfo.order : null,
  sortDirections: ["ascend", "descend"],
  ellipsis: true,
},

    {
      title: "Amount (₹)",
      dataIndex: "voucher_amount",
      key: "voucher_amount",
      width: 150,
      filters: Array.from(new Set(Vouchers2.map((s) => s.voucher_amount))).map(
        (amount) => ({
          text: amount,
          value: amount,
        })
      ),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.voucher_amount || null,
      ellipsis: true,
      render: (_, record) => <div>{record.voucher_amount}</div>,
    },
    {
  title: "Adjust Bill",
  dataIndex: "adjust_bill",
  key: "adjust_bill",
  width: 120,
  sorter: (a, b) =>
    (a.adjust_bill ?? 0) - (b.adjust_bill ?? 0),
  sortOrder:
    sortedInfo.columnKey === "adjust_bill" ? sortedInfo.order : null,
  sortDirections: ["ascend", "descend"],
  ellipsis: true,
  render: (_, record) => {
    if (record.adjust_bill === null || record.adjust_bill === undefined) {
      return <span style={{ color: "#999" }}>—</span>;
    }

    return (
      <span style={{ fontWeight: 500, color: "#1677ff" }}>
        ₹ {Number(record.adjust_bill).toLocaleString("en-IN")}
      </span>
    );
  },
},
  ];

  // Conditionally add action column
  if (
    currentModule?.right_edit === "Y" ||
    currentModule?.right_delete === "Y"
  ) {
    columns.push({
      title: "Action",
      width: 200,
      render: (_: any, record: Vouchers2) => (
        <Space size="middle">
          {currentModule?.right_edit === "Y" && (
            <>
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
              <Button
                onClick={() => handleDownloadVoucher(record.voucher_id)}
                className="px-8"
              >
                Download
              </Button>
            </>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: "flex",
          marginBottom: 16,
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Breadcrumb style={{ display: "inline-block", margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Vouchers2</Breadcrumb.Item>
        </Breadcrumb>

        <Flex
          gap="small"
          justify="space-between"
          align="center"
          style={{ marginBottom: 16 }}
        >
          <Space direction="vertical" size={12}>
            <RangePicker
              // disabledDate={disabledDate}
              onChange={handleDateRangeChange}
              format="DD-MM-YYYY"
            />
          </Space>
          <Select
            defaultValue={voucherType}
            onChange={handleVoucherTypeChange}
            style={{ width: 200, height: 38 }}
          >
            <Option value="">All</Option>
            <Option value="debit">Debit</Option>
            <Option value="credit">Credit</Option>
          </Select>

          {currentModule?.right_add === "Y" && (
            <Button type="primary" onClick={showCreateModal}>
              Add Voucher2
            </Button>
          )}
        </Flex>
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Vouchers2>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="voucher_id"
          dataSource={Vouchers2.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: "No Voucher2 found" }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(
                  pagination.current * pagination.pageSize,
                  Vouchers2.length
                )}{" "}
                of {Vouchers2.length}
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={(page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize,
                  }));
                  fetchVoucher(page, pageSize);
                }}
              />
            </Flex>
          )}
        />
      </div>

      <Voucher2Model
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Vouchers2;
